import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import CreateClientDialog from "@/components/CreateClientDialog";
import MigrateToBunnyDialog from "@/components/MigrateToBunnyDialog";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowUpRight, Users, FileCheck, Clock, AlertTriangle, XCircle, Trash2, Pencil, BarChart3, CheckCircle2, KanbanSquare, CalendarClock, Trash, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import AuthImage from "@/components/AuthImage";
import EditClientDialog from "@/components/EditClientDialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const STATS_BLOCKS = [
  { key: "total", label: "Posts totais", color: "#3B2B23", icon: FileCheck },
  { key: "approved", label: "Aprovados", color: "#2C543B", icon: FileCheck },
  { key: "pending", label: "Pendentes", color: "#805B10", icon: Clock },
  { key: "changes_requested", label: "Alterações", color: "#C07C5B", icon: AlertTriangle },
  { key: "rejected", label: "Não aprovados", color: "#8A2A22", icon: XCircle },
  { key: "scheduled", label: "Agendados", color: "#1E4A75", icon: CalendarClock },
  { key: "published", label: "Publicados", color: "#1E5A2C", icon: CheckCircle2 },
];

export default function AdminDashboard() {
  const [clients, setClients] = useState([]);
  const [stats, setStats] = useState({ total: 0, approved: 0, pending: 0, changes_requested: 0, rejected: 0, scheduled: 0, published: 0 });
  const [loading, setLoading] = useState(true);
  const [editingClient, setEditingClient] = useState(null);
  const [drillStatus, setDrillStatus] = useState(null); // {key, label, color} or null
  const [drillPosts, setDrillPosts] = useState([]);
  const [drillLoading, setDrillLoading] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();

  const clientNameById = clients.reduce((acc, c) => { acc[c.id] = c.name; return acc; }, {});
  const monthLabel = (m) => ["Jan","Fev","Mar","Abr","Mai","Jun","Jul","Ago","Set","Out","Nov","Dez"][((m||1)-1)] || "?";

  const openDrill = async (block) => {
    setDrillStatus(block);
    setDrillLoading(true);
    setDrillPosts([]);
    try {
      const params = block.key === "total" ? {} : { status: block.key };
      const r = await api.get("/posts", { params });
      setDrillPosts(r.data);
    } catch {
      toast.error("Erro ao carregar posts");
    } finally {
      setDrillLoading(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const [c, s] = await Promise.all([api.get("/clients"), api.get("/stats")]);
      setClients(c.data);
      setStats(s.data);
    } catch {
      toast.error("Erro ao carregar dashboard");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const [trashOpen, setTrashOpen] = useState(false);
  const [trashItems, setTrashItems] = useState([]);
  const [trashLoading, setTrashLoading] = useState(false);

  const openTrash = async () => {
    setTrashOpen(true);
    setTrashLoading(true);
    try {
      const r = await api.get("/clients/trash");
      setTrashItems(r.data);
    } catch { toast.error("Erro ao abrir a lixeira"); } finally { setTrashLoading(false); }
  };

  const restoreClient = async (id) => {
    try {
      await api.post(`/clients/${id}/restore`);
      toast.success("Cliente restaurado");
      openTrash();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao restaurar");
    }
  };

  const hardDeleteClient = async (c) => {
    if (!window.confirm(`Eliminar DEFINITIVAMENTE ${c.name} e ${c.posts_count} post(s)? Esta ação não pode ser desfeita.`)) return;
    try {
      await api.delete(`/clients/${c.id}/permanent`);
      toast.success("Eliminado definitivamente");
      openTrash();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao eliminar");
    }
  };

  const daysRemaining = (deletedAt) => {
    try {
      const then = new Date(deletedAt).getTime();
      const now = Date.now();
      const left = 30 - Math.floor((now - then) / (24 * 3600 * 1000));
      return Math.max(0, left);
    } catch { return 0; }
  };

  const deleteClient = async (id) => {
    try {
      await api.delete(`/clients/${id}`);
      toast.success("Cliente eliminado");
      load();
    } catch {
      toast.error("Erro ao eliminar");
    }
  };

  return (
    <Layout
      subtitle={user?.role === "admin" ? "Painel do Gestor Global" : `Painel · ${user?.profession || "Equipa"}`}
      actions={(
        <div className="flex flex-wrap gap-2 sm:gap-3 w-full md:w-auto">
          <Button
            variant="outline"
            onClick={() => navigate("/tarefas")}
            className="rounded-full border-[#3B2B23] text-[#3B2B23] hover:bg-[#EAE3DB] h-10 sm:h-11 px-4 sm:px-5 text-[10px] sm:text-xs tracking-[0.2em] uppercase flex-1 md:flex-none"
            data-testid="open-tasks-button"
          >
            <KanbanSquare className="w-4 h-4 mr-2" /> Tarefas
          </Button>
          {user?.role === "admin" && (
            <>
              <Button
                variant="outline"
                onClick={openTrash}
                className="rounded-full border-[#3B2B23] text-[#3B2B23] hover:bg-[#EAE3DB] h-10 sm:h-11 px-4 sm:px-5 text-[10px] sm:text-xs tracking-[0.2em] uppercase flex-1 md:flex-none"
                data-testid="open-trash-button"
              >
                <Trash className="w-4 h-4 mr-2" /> Lixeira
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate("/admin/equipa")}
                className="rounded-full border-[#3B2B23] text-[#3B2B23] hover:bg-[#EAE3DB] h-10 sm:h-11 px-4 sm:px-5 text-[10px] sm:text-xs tracking-[0.2em] uppercase flex-1 md:flex-none"
                data-testid="manage-team-button"
              >
                <Users className="w-4 h-4 mr-2" /> Equipa
              </Button>
              <MigrateToBunnyDialog />
              <CreateClientDialog onCreated={load} />
            </>
          )}
        </div>
      )}
    >

      {/* Stats */}
      <section className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-3 sm:gap-4 mb-10 sm:mb-14" data-testid="admin-stats-grid">
        {STATS_BLOCKS.map((b) => {
          const Icon = b.icon;
          const count = stats[b.key] ?? 0;
          return (
            <button
              key={b.key}
              type="button"
              onClick={() => openDrill(b)}
              disabled={count === 0}
              className="text-left disabled:cursor-not-allowed disabled:opacity-70 enabled:cursor-pointer"
              data-testid={`stat-${b.key}`}
            >
              <Card
                className="card-soft p-4 sm:p-5 transition-all enabled:hover:-translate-y-0.5 h-full"
              >
                <div className="flex items-center justify-between text-[#5C4A43]">
                  <span className="text-[9px] sm:text-[10px] uppercase tracking-[0.2em]">{b.label}</span>
                  <Icon className="w-4 h-4" style={{ color: b.color }} />
                </div>
                <div className="mt-2 sm:mt-3 text-3xl sm:text-4xl text-[#1A1412] font-bold">{count}</div>
                {count > 0 && (
                  <div className="mt-1 text-[9px] uppercase tracking-widest text-[#8B5E3C]">Ver posts →</div>
                )}
              </Card>
            </button>
          );
        })}
      </section>

      {/* Clients list */}
      <section>
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl sm:text-3xl text-[#1A1412] flex items-center gap-3">
            <Users className="w-5 h-5 text-[#8B5E3C]" /> Os meus clientes
          </h2>
          <span className="text-[10px] uppercase tracking-widest text-[#5C4A43]">{clients.length} ativos</span>
        </div>

        {loading ? (
          <div className="text-[#5C4A43] py-12 text-center text-sm uppercase tracking-widest">A carregar…</div>
        ) : clients.length === 0 ? (
          <div className="card-soft p-12 text-center" data-testid="no-clients-empty">
            <div className="text-2xl text-[#1A1412]">Sem clientes registados</div>
            <div className="mt-2 text-[#5C4A43] text-sm">Posso começar por adicionar o meu primeiro cliente.</div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {clients.map((c) => (
              <Card
                key={c.id}
                className="card-soft p-6 group cursor-pointer hover:-translate-y-0.5 transition-all"
                onClick={() => navigate(`/cliente/${c.id}`)}
                data-testid={`client-card-${c.id}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-4">
                    {c.logo_path ? (
                      <div className="w-12 h-12 rounded-full bg-white border border-[#EAE3DB] overflow-hidden flex items-center justify-center">
                        <AuthImage path={c.logo_path} className="w-full h-full object-contain p-1.5" />
                      </div>
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-[#3B2B23] text-[#F9F6F0] flex items-center justify-center text-lg font-bold">
                        {c.name?.[0]?.toUpperCase() || "?"}
                      </div>
                    )}
                    <div>
                      <div className="font-bold text-[#1A1412] tracking-wide">{c.name}</div>
                      <div className="text-[10px] text-[#5C4A43] mt-0.5 uppercase tracking-widest">{c.company || c.email}</div>
                    </div>
                  </div>
                  <ArrowUpRight className="w-4 h-4 text-[#8B5E3C] opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
                <div className="mt-6 flex items-center justify-between text-[10px] text-[#5C4A43]">
                  <span className="font-mono truncate">{c.email}</span>
                  {user?.role === "admin" && (
                    <div className="flex items-center gap-1">
                      <button
                        onClick={(e) => { e.stopPropagation(); navigate(`/admin/dashboard/${c.id}`); }}
                        className="p-1.5 rounded-full hover:bg-[#EAE3DB] text-[#3B2B23]"
                        data-testid={`open-dashboard-${c.id}`}
                        aria-label="Abrir dashboard do cliente"
                        title="Dashboard de performance"
                      >
                        <BarChart3 className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); setEditingClient(c); }}
                        className="p-1.5 rounded-full hover:bg-[#EAE3DB] text-[#3B2B23]"
                        data-testid={`edit-client-${c.id}`}
                        aria-label="Editar cliente"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <button
                            onClick={(e) => e.stopPropagation()}
                            className="p-1.5 rounded-full hover:bg-[#FBE6E4] text-[#8A2A22]"
                            data-testid={`delete-client-${c.id}`}
                            aria-label="Eliminar cliente"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </AlertDialogTrigger>
                        <AlertDialogContent className="bg-[#F9F6F0]">
                          <AlertDialogHeader>
                            <AlertDialogTitle>Eliminar {c.name}?</AlertDialogTitle>
                            <AlertDialogDescription>
                              O cliente e todos os posts associados vão para a lixeira e ficam recuperáveis durante 30 dias antes de serem apagados definitivamente.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel onClick={(e) => e.stopPropagation()}>Cancelar</AlertDialogCancel>
                            <AlertDialogAction
                              className="bg-[#8A2A22] hover:bg-[#6E2019] text-white"
                              onClick={(e) => { e.stopPropagation(); deleteClient(c.id); }}
                            >
                              Eliminar
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  )}
                </div>
              </Card>
            ))}
          </div>
        )}
      </section>

      <EditClientDialog
        open={!!editingClient}
        onOpenChange={(v) => !v && setEditingClient(null)}
        client={editingClient}
        onSaved={load}
      />

      <Dialog open={trashOpen} onOpenChange={setTrashOpen}>
        <DialogContent className="bg-[#F9F6F0] max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="trash-dialog">
          <DialogHeader>
            <DialogTitle className="text-2xl text-[#1A1412] flex items-center gap-3">
              <Trash className="w-5 h-5 text-[#8A2A22]" />
              Lixeira
              <span className="text-sm text-[#8B5E3C] font-normal">({trashItems.length})</span>
            </DialogTitle>
            <DialogDescription className="text-xs text-[#5C4A43]">
              Clientes eliminados. São automaticamente apagados definitivamente 30 dias após a eliminação.
            </DialogDescription>
          </DialogHeader>
          {trashLoading ? (
            <div className="text-center py-12 text-[#5C4A43] text-sm uppercase tracking-widest">A carregar…</div>
          ) : trashItems.length === 0 ? (
            <div className="text-center py-12 text-[#8B5E3C] italic">A lixeira está vazia.</div>
          ) : (
            <div className="space-y-2 mt-2">
              {trashItems.map((c) => {
                const days = daysRemaining(c.deleted_at);
                return (
                  <div key={c.id} className="p-3 rounded-lg border border-[#EAE3DB] bg-white flex items-center gap-3" data-testid={`trash-item-${c.id}`}>
                    <div className="w-12 h-12 rounded-full bg-[#EAE3DB] overflow-hidden flex-shrink-0 flex items-center justify-center">
                      {c.logo_path ? (
                        <AuthImage path={c.logo_path} className="w-full h-full object-cover" />
                      ) : (
                        <span className="text-[#3B2B23] font-bold">{(c.name || "?")[0]?.toUpperCase()}</span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-[#1A1412] truncate">{c.name}</div>
                      <div className="text-[10px] uppercase tracking-widest text-[#8B5E3C] mt-0.5 flex flex-wrap gap-x-3">
                        {c.company && <span>{c.company}</span>}
                        <span>{c.email}</span>
                        <span>{c.posts_count} post(s)</span>
                      </div>
                      <div className={`text-[10px] uppercase tracking-widest mt-1 ${days <= 3 ? "text-[#8A2A22]" : "text-[#8B5E3C]"}`}>
                        {days > 0 ? `Purgado em ${days} dia${days === 1 ? "" : "s"}` : "Vai ser purgado brevemente"}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button
                        onClick={() => restoreClient(c.id)}
                        className="p-2 rounded-full hover:bg-[#E1EDE4] text-[#2C543B]"
                        title="Restaurar"
                        data-testid={`restore-client-${c.id}`}
                      >
                        <RotateCcw className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => hardDeleteClient(c)}
                        className="p-2 rounded-full hover:bg-[#FBE6E4] text-[#8A2A22]"
                        title="Eliminar definitivamente"
                        data-testid={`purge-client-${c.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!drillStatus} onOpenChange={(v) => !v && setDrillStatus(null)}>
        <DialogContent className="bg-[#F9F6F0] max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="stat-drill-dialog">
          <DialogHeader>
            <DialogTitle className="text-2xl text-[#1A1412] flex items-center gap-3">
              {drillStatus && (
                <span className="w-3 h-3 rounded-full" style={{ background: drillStatus.color }} />
              )}
              {drillStatus?.label}
              <span className="text-sm text-[#8B5E3C] font-normal">({drillPosts.length})</span>
            </DialogTitle>
            <DialogDescription className="text-xs text-[#5C4A43]">
              Posts com este estado em todos os clientes. Clica num post para abrir os detalhes.
            </DialogDescription>
          </DialogHeader>

          {drillLoading ? (
            <div className="text-center py-12 text-[#5C4A43] text-sm uppercase tracking-widest">A carregar…</div>
          ) : drillPosts.length === 0 ? (
            <div className="text-center py-12 text-[#8B5E3C] italic">Sem posts neste estado.</div>
          ) : (
            <div className="space-y-2 mt-2" data-testid="stat-drill-list">
              {drillPosts.map((p) => (
                <button
                  key={p.id}
                  onClick={() => { setDrillStatus(null); navigate(`/post/${p.id}`); }}
                  className="w-full text-left p-3 rounded-lg border border-[#EAE3DB] bg-white hover:bg-[#F1EAE0] transition-colors flex gap-3"
                  data-testid={`drill-post-${p.id}`}
                >
                  {p.images?.[0]?.path ? (
                    <div className="w-16 h-16 rounded bg-[#EAE3DB] overflow-hidden flex-shrink-0">
                      <AuthImage path={p.images[0].path} className="w-full h-full object-cover" />
                    </div>
                  ) : (
                    <div className="w-16 h-16 rounded bg-[#EAE3DB] flex-shrink-0 flex items-center justify-center text-[#8B5E3C]">
                      <FileCheck className="w-6 h-6" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 text-[10px] uppercase tracking-widest text-[#8B5E3C]">
                      <span className="font-bold text-[#3B2B23]">{clientNameById[p.client_id] || "—"}</span>
                      <span>·</span>
                      <span>{monthLabel(p.month)} {p.year}{p.day ? ` · dia ${String(p.day).padStart(2, "0")}` : ""}</span>
                      {p.post_type && (
                        <>
                          <span>·</span>
                          <span className="capitalize">{p.post_type}</span>
                        </>
                      )}
                    </div>
                    <div className="mt-1 text-sm text-[#1A1412] line-clamp-2 whitespace-pre-wrap">
                      {p.copy?.trim() || <span className="italic text-[#8B5E3C]">Sem copy definida</span>}
                    </div>
                  </div>
                  <ArrowUpRight className="w-4 h-4 text-[#8B5E3C] flex-shrink-0 self-center" />
                </button>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
